import {
  useFinishSignupMutation,
  useRequestPhoneVerificationTokenMutation,
  useVerifyPhoneMutation
} from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { Button } from 'src/components/button/button'
import { CodeInput } from 'src/components/code_input/code_input'
import { SvgIcon } from 'src/components/icons/svg-icon'
import { ProgressIndicator } from 'src/components/progress_indicator/progress_indicator'
import { CLASS_PREFIX } from 'src/constants'
import { textResources } from 'src/lang/de'
import { AUTH_CLS } from '../../auth/constants'
import './phone_code_verification.scss'

type IProps = {
  onBack: () => void
  phoneNumber: string
  onComplete?: (profileId?: string) => void
}

const cls = `${CLASS_PREFIX}phone-code-verification`

export const PhoneCodeVerification: React.FC<IProps> = ({ onBack, phoneNumber, onComplete }) => {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [verifyPhone] = useVerifyPhoneMutation()
  const [finishSignup] = useFinishSignupMutation()
  const [requestPhoneVerificationToken] = useRequestPhoneVerificationTokenMutation()

  const handleVerify = useCallback(async () => {
    if (!code || code.length < 5) return

    setIsLoading(true)
    setErrorMessage('')

    try {
      const res = await verifyPhone({ variables: { input: { token: code } } })

      if (res.data?.verifyPhone?.errors?.length) {
        throw res.data.verifyPhone.errors[0]
      }

      const result = await finishSignup({ variables: { input: {} } })
      const profileId = result.data?.finishSignup?.profileId ?? undefined
      onComplete?.(profileId)
    } catch {
      setIsLoading(false)
      setErrorMessage(textResources.onboardingSettings.invalidVerificationCode)
    }
  }, [code, verifyPhone, finishSignup])

  const handleResendCode = useCallback(async () => {
    setErrorMessage('')
    try {
      const res = await requestPhoneVerificationToken({
        variables: { input: { newPhone: phoneNumber } }
      })
      if (res.data?.requestPhoneVerificationToken?.errors?.length) {
        throw res.data.requestPhoneVerificationToken.errors[0]
      }
    } catch {
      setErrorMessage(textResources.shared.errorUnknown)
    }
  }, [phoneNumber, requestPhoneVerificationToken])

  return (
    <div className={cls}>
      <div className={`${cls}__header`}>
        <button onClick={onBack}>
          <SvgIcon name="Arrows/chevron-left" /> {textResources.shared.back}
        </button>
        <ProgressIndicator steps={3} current={2} />
      </div>
      <div className={`${cls}__content`}>
        <div>
          <div className={`${AUTH_CLS}__header`}>{textResources.authentication.verifyPhoneTitle}</div>
          <div className={`${AUTH_CLS}__subheader`}>
            {textResources.authentication.verifyPhoneSubtitle(phoneNumber)}
          </div>
        </div>
        <button className={`${cls}__edit-phone`} onClick={onBack}>
          <span className={`${cls}__edit-phone__label`}>{textResources.authentication.editPhoneNumber} <SvgIcon name="General/edit-05" size={16} /></span>
        </button>
        <CodeInput digits={5} label={textResources.authentication.verificationCode} value={code} onChange={setCode} />
        {errorMessage && <div className={`${cls}__error`}>{errorMessage}</div>}
        <button className={`${cls}__resend-code`} onClick={handleResendCode}>
          <span className={`${cls}__resend-code__label`}>{textResources.authentication.resendCode}</span>
        </button>
        <Button
          className={`${cls}__verify`}
          size="large"
          title={textResources.authentication.verifyPhoneNumber}
          disabled={isLoading || code.length < 5}
          onClick={handleVerify}
        />
      </div>
    </div>
  )
}
